import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatContext = createContext(null);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  // Core state
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Refs for stability
  const activeConvRef = useRef(null);
  const initDone = useRef(false);

  // Keep ref in sync
  useEffect(() => {
    activeConvRef.current = activeConversation;
    if (activeConversation) {
      localStorage.setItem('cdsur_active_conv_id', activeConversation.id);
    }
  }, [activeConversation]);

  // Persist messages
  useEffect(() => {
    if (activeConversation && messages.length > 0) {
      localStorage.setItem(`cdsur_msgs_${activeConversation.id}`, JSON.stringify(messages.slice(-50)));
    }
  }, [messages, activeConversation]);

  // Initialize on mount
  useEffect(() => {
    if (initDone.current) return;
    initDone.current = true;
    
    const init = async () => {
      try {
        // Fetch conversations
        const response = await axios.get(`${API}/conversations`);
        setConversations(response.data || []);
        
        // Restore active conversation
        const savedId = localStorage.getItem('cdsur_active_conv_id');
        if (savedId && response.data?.length > 0) {
          const found = response.data.find(c => c.id === savedId);
          if (found) {
            // Load full conversation with messages
            try {
              const convResp = await axios.get(`${API}/conversations/${savedId}`);
              setActiveConversation(convResp.data);
              setMessages(convResp.data.messages || []);
            } catch (e) {
              console.error('Error loading saved conversation:', e);
              // Try to restore from localStorage
              const savedMsgs = localStorage.getItem(`cdsur_msgs_${savedId}`);
              if (savedMsgs) {
                setActiveConversation(found);
                setMessages(JSON.parse(savedMsgs));
              }
            }
          }
        }
      } catch (error) {
        console.error('Error initializing chat:', error);
      }
    };
    
    init();
  }, []);

  // Fetch conversations list
  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/conversations`);
      setConversations(response.data || []);
      return response.data;
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return [];
    }
  }, []);

  // Select a conversation
  const selectConversation = useCallback(async (conversationId) => {
    if (!conversationId) {
      setActiveConversation(null);
      setMessages([]);
      return;
    }

    // Don't reload if already selected
    if (activeConvRef.current?.id === conversationId) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}`);
      setActiveConversation(response.data);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching conversation:', error);
      // Try localStorage fallback
      const savedMsgs = localStorage.getItem(`cdsur_msgs_${conversationId}`);
      if (savedMsgs) {
        setMessages(JSON.parse(savedMsgs));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new conversation
  const createConversation = useCallback(async (title = 'Nueva conversación') => {
    try {
      const response = await axios.post(`${API}/conversations`, { title });
      const newConv = response.data;
      setConversations(prev => [newConv, ...prev]);
      setActiveConversation(newConv);
      setMessages([]);
      return newConv;
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  }, []);

  // Delete conversation
  const deleteConversation = useCallback(async (conversationId) => {
    try {
      await axios.delete(`${API}/conversations/${conversationId}`);
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      if (activeConvRef.current?.id === conversationId) {
        setActiveConversation(null);
        setMessages([]);
        localStorage.removeItem('cdsur_active_conv_id');
        localStorage.removeItem(`cdsur_msgs_${conversationId}`);
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  }, []);

  // Send message - THE MAIN FUNCTION
  const sendMessage = useCallback(async (messageText, conversationId = null, quickActionId = null) => {
    setSendingMessage(true);
    
    // Optimistic update - add user message immediately
    const userMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      const targetConvId = conversationId || activeConvRef.current?.id;
      const startTime = Date.now();
      
      // Call chat API with long timeout
      const response = await axios.post(`${API}/chat`, {
        message: messageText,
        conversation_id: targetConvId,
        quick_action_id: quickActionId
      }, {
        timeout: 900000  // 15 minutes for complex queries
      });

      const endTime = Date.now();
      const latencyMs = endTime - startTime;

      const { conversation_id: newConvId, response: assistantMsg, message: savedUserMsg } = response.data;
      assistantMsg.latencyMs = latencyMs;

      // Update messages - replace temp with real
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== userMessage.id);
        return [...filtered, savedUserMsg, assistantMsg];
      });

      // If new conversation was created, update state
      if (!targetConvId && newConvId) {
        await fetchConversations();
        // Load the new conversation
        try {
          const convResp = await axios.get(`${API}/conversations/${newConvId}`);
          setActiveConversation(convResp.data);
        } catch (e) {
          console.error('Error loading new conversation:', e);
        }
      } else {
        // Update conversation list to reflect new message
        setConversations(prev => 
          prev.map(c => 
            c.id === newConvId 
              ? { ...c, updated_at: new Date().toISOString(), message_count: (c.message_count || 0) + 2 }
              : c
          ).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
        );
        
        // Update title if it was generic
        const currentTitle = activeConvRef.current?.title || '';
        if (currentTitle === 'Nueva conversación' || currentTitle.toLowerCase().includes('test')) {
          try {
            const convResp = await axios.get(`${API}/conversations/${newConvId}`);
            setActiveConversation(convResp.data);
            setConversations(prev => 
              prev.map(c => c.id === newConvId ? { ...c, title: convResp.data.title } : c)
            );
          } catch (e) {
            // Ignore title update errors
          }
        }
      }

      return response.data;
    } catch (error) {
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
      console.error('Error sending message:', error);
      
      // Add error message
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: `Error al procesar la consulta: ${error.message || 'Error de conexión'}. Por favor, intentá de nuevo.`,
        timestamp: new Date().toISOString()
      }]);
      
      throw error;
    } finally {
      setSendingMessage(false);
    }
  }, [fetchConversations]);

  // Start new chat
  const startNewChat = useCallback(() => {
    setActiveConversation(null);
    setMessages([]);
    localStorage.removeItem('cdsur_active_conv_id');
  }, []);

  const value = {
    conversations,
    activeConversation,
    messages,
    loading,
    sendingMessage,
    fetchConversations,
    selectConversation,
    createConversation,
    deleteConversation,
    sendMessage,
    startNewChat
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};
